import { useState, useEffect } from "react";
import { Radio, BarChart3, Brain, MessageSquare, Hash, Volume2 } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";

const PERKS = [
  { icon: Radio, title: "Weekly Live Trading Sessions", desc: "Trade alongside working pros in real time." },
  { icon: BarChart3, title: "Daily Market Analysis", desc: "Institutional-grade breakdowns before the session opens." },
  { icon: Brain, title: "Psychology Coaching", desc: "Master the mental game that blows most accounts." },
  { icon: MessageSquare, title: "Direct Mentor Access", desc: "Ask working prop traders anything, anytime." },
];

const MESSAGES = [
  ["Marcus · Mentor", "London open in 20. Watching XAU/USD liquidity sweep at 2,412 — plan posted in #setups.", "#FFD700"],
  ["Priya · Analyst", "CPI at 8:30 ET. Remember funded accounts: no new trades ±5 min around release.", "#2DACE2"],
  ["Dan · Trader", "Passed Phase 2 today. The psych calls genuinely changed how I size. Thank you.", "#22C55E"],
];

const OnlineCounter = () => {
  const [count, setCount] = useState(2347);
  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => Math.min(2600, Math.max(2200, c + Math.floor(Math.random() * 11) - 5)));
    }, 3500);
    return () => clearInterval(id);
  }, []);
  return (
    <span
      data-testid="discord-online-counter"
      className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/25 px-2.5 py-0.5"
    >
      <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
      <span className="text-[10px] font-bold text-[#22C55E] tabular-nums">
        {count.toLocaleString()} online
      </span>
    </span>
  );
};

export default function DiscordSpotlight() {
  return (
    <section id="mentorship" className="py-24 lg:py-32 relative overflow-hidden" data-testid="discord-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#2DACE2]/[0.05] blur-[140px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center relative">
        <Reveal>
          <Chapter index={5} label="The Edge" />
          <div className="inline-flex items-center gap-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 px-4 py-1.5 mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.15em] gold-text">
              $997/yr Value — Included Free
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.04em] text-white leading-[1.15]">
            Capital is a commodity.<br />
            <span className="gold-text">Mentorship is the edge.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#999999] leading-relaxed max-w-lg">
            The moment you buy any challenge, you're inside the MyFXPocket Traders Discord — working
            pro mentors, live sessions, and a community built to get you funded. Yours whether you
            pass, fail, or re-take.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {PERKS.map((p) => (
              <div key={p.title} className="flex items-start gap-3" data-testid={`mentorship-perk-${p.title.split(" ")[0].toLowerCase()}`}>
                <div className="w-9 h-9 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/20 flex items-center justify-center shrink-0">
                  <p.icon size={16} className="text-[#FFD700]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{p.title}</div>
                  <div className="text-xs text-[#999999] mt-1 leading-relaxed">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <a
            href="https://discord.gg/fkm3rwvHVB"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mentorship-cta-button"
            className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold mt-10"
          >
            Unlock Free Mentorship
          </a>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="glass-card rounded-2xl overflow-hidden float-slow" data-testid="discord-mockup">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/5 bg-[#030B16]/70">
              <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#F1B434]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]/70" />
              <span className="ml-3 text-xs font-semibold text-[#999999]">MyFXPocket Traders — Discord</span>
              <OnlineCounter />
            </div>
            <div className="grid grid-cols-[130px_1fr] min-h-[300px]">
              <div className="bg-[#030B16]/80 border-r border-white/5 p-3 space-y-1">
                {["announcements", "market-analysis", "setups", "psychology", "wins"].map((c) => (
                  <div key={c} className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-[#999999] hover:bg-white/5 hover:text-white transition-colors">
                    <Hash size={11} /> {c}
                  </div>
                ))}
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-[#999999]">
                  <Volume2 size={11} /> Live Trading Floor
                  <span className="live-dot w-1.5 h-1.5 rounded-full bg-[#22C55E] ml-auto" />
                </div>
              </div>
              <div className="p-4 space-y-4">
                {MESSAGES.map(([name, msg, color]) => (
                  <div key={name} className="flex gap-3">
                    <div
                      className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-[#0D0E13]"
                      style={{ background: color }}
                    >
                      {name[0]}
                    </div>
                    <div>
                      <div className="text-xs font-semibold" style={{ color }}>{name}</div>
                      <p className="text-xs text-[#DBDFE8] mt-0.5 leading-relaxed">{msg}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
